import type { Editor } from '@tiptap/core';
import * as React from 'react';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';

// Mirrors the server's RequestSizeLimit on POST attachment/image (decimal 20 MB per request);
// each image uploads in its own request so the limit applies per file.
export const INLINE_IMAGE_BYTES_MAX = 20_000_000;

const findImagePos = (editor: Editor, src: string): number | null => {
	let found: number | null = null;
	editor.state.doc.descendants((node, pos) => {
		if (found != null) return false;
		if (node.type.name === 'image' && node.attrs.src === src) {
			found = pos;
			return false;
		}
		return true;
	});
	return found;
};

const replaceImageSrc = (editor: Editor, placeholderUrl: string, hostedUrl: string) => {
	const pos = findImagePos(editor, placeholderUrl);
	if (pos == null) return;
	const node = editor.state.doc.nodeAt(pos);
	if (!node) return;
	editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: hostedUrl }));
};

const removeImage = (editor: Editor, placeholderUrl: string) => {
	const pos = findImagePos(editor, placeholderUrl);
	if (pos == null) return;
	const node = editor.state.doc.nodeAt(pos);
	if (!node) return;
	editor.view.dispatch(editor.state.tr.delete(pos, pos + node.nodeSize));
};

/**
 * Inline images for the composer: each pasted/dropped image is inserted immediately with a local
 * blob URL as a visible placeholder, uploaded to attachment/image, then the placeholder src is
 * swapped for the hosted public URL (or the node is removed and an error surfaced on failure).
 * Sends stay disabled while any upload is in flight so a blob URL can never leave the editor.
 */
export const useComposerInlineImages = (editor: Editor | null) => {
	const [uploadingImageCount, setUploadingImageCount] = React.useState(0);
	// Support has no MutationCache/toast, so failed uploads must surface inline (same rationale as
	// useComposerAttachments); per-upload callbacks handle the rest.
	const [imageUploadError, setImageUploadError] = React.useState<string | null>(null);
	const uploadImage = useImageAttachmentMutation();

	const insertImages = (files: File[], insertAt?: number) => {
		if (!editor) return;
		setImageUploadError(null);

		const accepted: { file: File; placeholderUrl: string }[] = [];
		for (const file of files) {
			if (!file.type.startsWith('image/')) continue;
			if (file.size > INLINE_IMAGE_BYTES_MAX) {
				setImageUploadError(`"${file.name}" is larger than the 20 MB image limit.`);
				continue;
			}
			accepted.push({ file, placeholderUrl: URL.createObjectURL(file) });
		}
		if (accepted.length === 0) return;

		const imageNodes = accepted.map(({ file, placeholderUrl }) => ({
			type: 'image',
			attrs: { src: placeholderUrl, alt: file.name },
		}));
		if (insertAt != null) {
			editor.chain().focus().insertContentAt(insertAt, imageNodes).run();
		} else {
			editor.chain().focus().insertContent(imageNodes).run();
		}

		setUploadingImageCount(count => count + accepted.length);
		accepted.forEach(({ file, placeholderUrl }) => {
			const finishUpload = (apply: (activeEditor: Editor) => void) => {
				if (!editor.isDestroyed) apply(editor);
				URL.revokeObjectURL(placeholderUrl);
				setUploadingImageCount(count => count - 1);
			};
			uploadImage.mutate(
				{ files: [file] },
				{
					onSuccess: attachments => {
						const hostedUrl = attachments[0]?.url;
						finishUpload(activeEditor => {
							if (hostedUrl) {
								replaceImageSrc(activeEditor, placeholderUrl, hostedUrl);
							} else {
								removeImage(activeEditor, placeholderUrl);
								setImageUploadError(`Failed to upload "${file.name}". Please try again.`);
							}
						});
					},
					onError: error => {
						finishUpload(activeEditor => {
							removeImage(activeEditor, placeholderUrl);
							setImageUploadError(getApiErrorMessage(error, `Failed to upload "${file.name}". Please try again.`));
						});
					},
				}
			);
		});
	};

	const dismissImageUploadError = () => setImageUploadError(null);

	return {
		dismissImageUploadError,
		hasUploadingImages: uploadingImageCount > 0,
		imageUploadError,
		insertImages,
	};
};
